import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  ShieldCheck, Activity, Lock, Cpu, ArrowUpRight, Database,
  FileCheck2, Radar, Server, Fingerprint, ChevronRight, Mail, Loader2, Check, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Explainer } from "@/components/Explainer";

const CONTACT_EMAIL = "info@vaultvec.com";
const MAILTO = `mailto:${CONTACT_EMAIL}?subject=Vaultvec%20enquiry`;

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

const HERO_IMG = "https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByb29tJTIwYmx1ZSUyMGxpZ2h0c3xlbnwwfHx8fDE3ODU3MDU4ODZ8MA&ixlib=rb-4.1.0&q=85";
const PHARMA_IMG = "https://images.unsplash.com/photo-1581093577421-f561a654a353?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxzY2llbnRpc3QlMjBsYWJvcmF0b3J5fGVufDB8fHx8MTc4NTcwNTg3N3ww&ixlib=rb-4.1.0&q=85";
const AI_IMG = "https://images.unsplash.com/photo-1727434032773-af3cd98375ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwbmV1cmFsJTIwbmV0d29yayUyMGdsb3dpbmd8ZW58MHx8fHwxNzg1NzA1ODY3fDA&ixlib=rb-4.1.0&q=85";

function Eyebrow({ children, color = "text-primary" }) {
  return <span className={`eyebrow-line font-mono text-xs tracking-[0.2em] uppercase ${color}`}>{children}</span>;
}

function Nav() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5">
          <div className="h-8 w-8 bg-primary flex items-center justify-center glow-primary">
            <ShieldCheck className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-lg font-extrabold tracking-tighter">VAULTVEC</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
          <a href="#platform" className="hover:text-foreground transition-colors">Platform</a>
          <a href="#compliance" className="hover:text-foreground transition-colors">Compliance</a>
          <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
          <Link to="/resources" className="hover:text-foreground transition-colors">Resources</Link>
          <a href="#why" className="hover:text-foreground transition-colors">Why Vaultvec</a>
          <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login-link">
            <Button variant="ghost" className="rounded-sm text-sm hover:bg-white/5">Sign in</Button>
          </Link>
          <Link to="/login">
            <Button data-testid="nav-start-btn" className="rounded-sm bg-primary hover:bg-primary/90 text-white font-medium glow-primary hover:glow-primary-strong transition-shadow">
              Start free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

const FEATURES = [
  { icon: Database, title: "Immutable Backup & Recovery", desc: "Air-gapped restore points with cryptographic verification for LIMS, MES, ERP and QC workloads.", span: "md:col-span-2" },
  { icon: Radar, title: "Active Threat Defense", desc: "Behavioral ransomware detection and anomaly monitoring across every protected asset.", span: "" },
  { icon: FileCheck2, title: "21 CFR Part 11 Vault", desc: "Tamper-evident, hash-chained audit trails and GxP data-integrity signatures built in.", span: "" },
  { icon: Cpu, title: "AI Threat Analysis", desc: "Vaultvec AI reasons over your fleet to prioritize remediation and flag backup anomalies.", span: "" },
  { icon: Fingerprint, title: "Role-Based Governance", desc: "Segregated duties for IT Admins, Compliance Officers and Auditors with full traceability.", span: "" },
  { icon: Server, title: "Hybrid & Multi-Cloud", desc: "Protect On-Prem, AWS, Azure and GCP workloads from a single command center.", span: "md:col-span-2" },
];

const COMPARISON = [
  { feature: "Purpose-built for pharma (GxP / GMP)", v: true, legacy: false },
  { feature: "FDA 21 CFR Part 11 vault built-in", v: true, legacy: "Manual / add-on" },
  { feature: "Tamper-evident hash-chained audit trail", v: true, legacy: false },
  { feature: "One-click chain integrity verification", v: true, legacy: false },
  { feature: "GxP electronic records & signatures", v: true, legacy: false },
  { feature: "AI-generated inspection-ready reports", v: true, legacy: false },
  { feature: "Roles: IT · Compliance · Auditor", v: true, legacy: "IT-only" },
  { feature: "Immutable backup & recovery", v: true, legacy: true },
  { feature: "Ransomware & threat defense", v: true, legacy: true },
  { feature: "Live in days, not months", v: true, legacy: false },
];

function Cell({ value }) {
  if (value === true) return <Check className="h-4 w-4 text-[hsl(var(--chart-2))] mx-auto" strokeWidth={2.5} />;
  if (value === false) return <X className="h-4 w-4 text-muted-foreground/40 mx-auto" strokeWidth={2} />;
  return <span className="font-mono text-[10px] text-muted-foreground">{value}</span>;
}

function Comparison() {
  return (
    <section id="why" className="max-w-7xl mx-auto px-6 py-28">
      <div className="max-w-2xl mb-16">
        <Eyebrow>Why Vaultvec</Eyebrow>
        <h2 className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl mt-5">Built for compliance, not adapted to it.</h2>
        <p className="mt-6 text-muted-foreground leading-relaxed">
          Legacy backup suites protect your data. Vaultvec protects your data <span className="text-foreground">and proves compliance</span> — because the audit trail, data-integrity signatures and 21 CFR Part 11 reporting are the product, not an add-on you assemble.
        </p>
      </div>

      <div className="rounded-sm border border-white/10 overflow-hidden">
        <div className="grid grid-cols-[1.6fr_1fr_1fr] bg-[#0a0a0c] border-b border-white/10">
          <div className="p-4 font-mono text-[10px] tracking-widest text-muted-foreground">CAPABILITY</div>
          <div className="p-4 text-center border-l border-white/10 bg-primary/10 relative">
            <div className="absolute inset-x-0 top-0 h-px bg-primary/60" />
            <span className="font-heading font-bold tracking-tight text-primary">Vaultvec</span>
          </div>
          <div className="p-4 text-center border-l border-white/10 font-mono text-[10px] tracking-widest text-muted-foreground flex items-center justify-center">LEGACY BACKUP SUITES</div>
        </div>
        {COMPARISON.map((row, i) => (
          <motion.div
            key={row.feature}
            initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: i * 0.03, duration: 0.3 }}
            className="grid grid-cols-[1.6fr_1fr_1fr] border-b border-white/10 last:border-0 hover:bg-white/[0.02] transition-colors"
          >
            <div className="p-4 text-sm text-foreground">{row.feature}</div>
            <div className="p-4 flex items-center justify-center border-l border-white/10 bg-primary/[0.05]"><Cell value={row.v} /></div>
            <div className="p-4 flex items-center justify-center border-l border-white/10"><Cell value={row.legacy} /></div>
          </motion.div>
        ))}
      </div>

      <p className="mt-6 font-mono text-[11px] text-muted-foreground tracking-wide">
        Acronis protects your data. Vaultvec makes your next FDA inspection a non-event.
      </p>
    </section>
  );
}

const TIERS = [
  { name: "Facility", price: "$1,290", unit: "/mo", tagline: "Single-site pharma teams", lookup_key: "facility_monthly", features: ["Up to 50 workloads", "Backup & recovery", "Threat monitoring", "Standard audit trail"], cta: "Subscribe", highlight: false },
  { name: "Enterprise GxP", price: "$4,900", unit: "/mo", tagline: "Multi-site regulated manufacturing", lookup_key: "enterprise_monthly", features: ["Unlimited workloads", "21 CFR Part 11 vault", "AI threat + anomaly engine", "Role-based governance", "Priority validated support"], cta: "Subscribe", highlight: true },
  { name: "Global", price: "Custom", unit: "", tagline: "Global pharma & CROs", lookup_key: null, features: ["Everything in Enterprise", "Dedicated compliance advisor", "GAMP 5 validation package", "Custom data-residency", "24/7 SOC integration"], cta: "Start free", highlight: false },
];

export default function Landing() {
  const [checkoutKey, setCheckoutKey] = useState(null);
  const [ce, setCe] = useState("");
  const [dl, setDl] = useState(false);

  const downloadChecklist = async (e) => {
    e.preventDefault();
    setDl(true);
    try {
      const { data } = await api.post("/resources/compliance-checklist", { email: ce }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "vaultvec-21cfr-part11-checklist.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Checklist downloaded — check your downloads folder.");
      setCe("");
    } catch (err) {
      toast.error("Please enter a valid work email and try again.");
    } finally {
      setDl(false);
    }
  };

  const handleCheckout = async (lookup_key) => {
    setCheckoutKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setCheckoutKey(null);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />
      <Nav />

      {/* HERO */}
      <section className="relative pt-16 min-h-[94vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Secure data center" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/85 to-background" />
          <div className="absolute inset-0 grid-texture opacity-50" />
          <div className="ambient-spot top-[-10%] right-[5%] w-[520px] h-[520px] opacity-70" />
          <div className="ambient-spot bottom-[10%] left-[-5%] w-[420px] h-[420px] opacity-40" />
        </div>
        <motion.div
          variants={stagger} initial="hidden" animate="show"
          className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-8 overflow-hidden">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--chart-2))] pulse-dot" />
            <span className="font-mono text-[11px] tracking-widest text-muted-foreground">FREE TO START · NO SALES CALL · NO CLOUD ACCOUNT NEEDED</span>
          </motion.div>
          <motion.h1 variants={fadeUp} className="font-heading font-extrabold tracking-tighter text-5xl sm:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
            Keep your critical data <span className="text-primary">safe, private and provable.</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-8 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Vaultvec is the secure vault for teams that can't afford to lose data. Back it up, lock it against tampering, and prove it's intact — in one simple platform. Built for pharma, ready for any team.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-4">
            <Link to="/login">
              <Button data-testid="hero-start-btn" size="lg" className="rounded-sm bg-primary hover:bg-primary/90 text-white font-medium h-12 px-8 glow-primary hover:glow-primary-strong transition-shadow">
                Start free <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
            <a href="#how">
              <Button data-testid="hero-how-btn" size="lg" variant="outline" className="rounded-sm border-white/15 h-12 px-8 hover:bg-white/5 hover:border-white/30 transition-colors">
                Watch it work
              </Button>
            </a>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 max-w-3xl glass rounded-sm overflow-hidden">
            {[["99.999%", "Recovery SLA"], ["<15min", "RTO target"], ["100%", "Chain integrity"], ["24/7", "SOC coverage"]].map(([n, l]) => (
              <div key={l} className="bg-black/40 p-5">
                <div className="font-heading text-2xl font-bold tracking-tight text-foreground">{n}</div>
                <div className="text-xs text-muted-foreground mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* TRUST BAR */}
      <section className="relative border-y border-white/10 bg-[#08080a]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          <span className="font-mono text-[11px] tracking-widest text-muted-foreground">TRUSTED-GRADE CONTROLS</span>
          {["21 CFR Part 11", "GAMP 5 / GxP", "AES-256 zero-knowledge", "Immutable WORM", "Hash-chained audit", "Your storage, your control"].map((t) => (
            <span key={t} className="flex items-center gap-1.5 text-xs text-foreground/80">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {t}
            </span>
          ))}
        </div>
      </section>

      {/* PLATFORM / FEATURES */}
      <section id="platform" className="max-w-7xl mx-auto px-6 py-28">
        <div className="max-w-2xl mb-16">
          <Eyebrow>The Platform</Eyebrow>
          <h2 className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl mt-5">One command center for protection & compliance</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }}
              className={`card-elevated rounded-sm p-8 group ${f.span}`}
            >
              <div className="h-11 w-11 rounded-sm bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:glow-primary transition-shadow">
                <f.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-bold mt-6 tracking-tight">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COMPLIANCE SPLIT */}
      <section id="compliance" className="relative border-y border-white/10 bg-[#08080a] overflow-hidden">
        <div className="ambient-spot top-1/2 left-[-8%] w-[420px] h-[420px] opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 py-28 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Eyebrow color="text-[hsl(var(--chart-2))]">Compliance Vault</Eyebrow>
            <h2 className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl mt-5">Prove data integrity, not just claim it.</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Every action is written to a hash-chained, tamper-evident ledger. Electronic signatures, GxP records and full audit trails are captured automatically — turning your next inspection into a non-event.
            </p>
            <ul className="mt-8 space-y-4">
              {["Cryptographically chained audit trail with live integrity verification", "Electronic records & signatures aligned to 21 CFR Part 11", "AI-generated inspection-ready compliance reports"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm">
                  <span className="h-6 w-6 rounded-sm bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <FileCheck2 className="h-3.5 w-3.5 text-[hsl(var(--chart-2))]" strokeWidth={1.5} />
                  </span>
                  <span className="text-muted-foreground">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-sm border border-white/10 overflow-hidden group">
            <img src={PHARMA_IMG} alt="Pharma lab" className="w-full h-[440px] object-cover opacity-70 group-hover:opacity-80 group-hover:scale-[1.02] transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute top-4 left-4 glass rounded-full px-3 py-1.5 flex items-center gap-2 font-mono text-[11px]">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--chart-2))] pulse-dot" />
              <span className="text-[hsl(var(--chart-2))]">CHAIN STATUS · VERIFIED</span>
            </div>
            <div className="absolute bottom-0 inset-x-0 glass border-t border-white/10 p-5 font-mono text-xs">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">INTEGRITY BREAKS</span>
                <span className="text-[hsl(var(--chart-2))]">0 · CHAIN INTACT</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI BLOCK */}
      <section className="relative max-w-7xl mx-auto px-6 py-28 grid lg:grid-cols-2 gap-16 items-center">
        <div className="relative rounded-sm border border-white/10 overflow-hidden order-2 lg:order-1 group">
          <img src={AI_IMG} alt="AI automation" className="w-full h-[440px] object-cover opacity-70 group-hover:opacity-85 transition-opacity duration-700" />
          <div className="absolute inset-0 grid-texture opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute top-4 left-4 glass rounded-full px-3 py-1.5 flex items-center gap-2 font-mono text-[11px]">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--chart-1))] pulse-dot" />
            <span className="text-[hsl(var(--chart-1))]">AI ENGINE · ONLINE</span>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <Eyebrow color="text-[hsl(var(--chart-1))]">Vaultvec AI</Eyebrow>
          <h2 className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl mt-5">An analyst that never sleeps.</h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Vaultvec AI continuously reasons over your protected fleet — surfacing backup anomalies, prioritizing threat remediation and drafting regulatory summaries in seconds, not weeks.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-3">
            {[["Threat triage", Radar], ["Anomaly scan", Activity], ["CFR reports", FileCheck2], ["Risk scoring", Lock]].map(([l, Icon]) => (
              <div key={l} className="card-elevated rounded-sm p-4 flex items-center gap-3">
                <Icon className="h-4 w-4 text-[hsl(var(--chart-1))]" strokeWidth={1.5} />
                <span className="text-sm">{l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / EXPLAINER */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-28">
        <div className="max-w-2xl mb-16">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl mt-5">Protect. Detect. Prove.</h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            A full start-to-finish walkthrough — from sign-in through every module — showing exactly how your team backs up, defends, proves compliance and exports a signed report.
          </p>
        </div>
        <Explainer />
      </section>

      <Comparison />

      {/* PRICING */}
      <section id="pricing" className="relative border-t border-white/10 bg-[#08080a] overflow-hidden">
        <div className="ambient-spot top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] opacity-25" />
        <div className="relative max-w-7xl mx-auto px-6 py-28">
          <div className="max-w-2xl mb-16">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl mt-5">Validated protection, transparent tiers</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {TIERS.map((t) => (
              <div
                key={t.name}
                data-testid={`pricing-${t.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative rounded-sm p-8 flex flex-col transition-all duration-300 ${
                  t.highlight
                    ? "border border-primary/60 bg-[#0d0a0a] lg:-translate-y-4 glow-primary z-10"
                    : "card-elevated"
                }`}
              >
                {t.highlight && (
                  <>
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
                    <span className="absolute -top-3 left-8 bg-primary text-white font-mono text-[10px] tracking-widest px-3 py-1 rounded-sm">MOST DEPLOYED</span>
                  </>
                )}
                <h3 className="font-heading text-xl font-bold tracking-tight">{t.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">{t.tagline}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="font-heading text-4xl font-extrabold tracking-tighter">{t.price}</span>
                  <span className="text-muted-foreground text-sm mb-1">{t.unit}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <ChevronRight className="h-4 w-4 text-primary shrink-0 mt-0.5" strokeWidth={1.5} /> {f}
                    </li>
                  ))}
                </ul>
                {t.lookup_key ? (
                  <Button
                    data-testid={`subscribe-${t.lookup_key}`}
                    onClick={() => handleCheckout(t.lookup_key)}
                    disabled={checkoutKey === t.lookup_key}
                    className={`w-full rounded-sm mt-8 ${t.highlight ? "bg-primary hover:bg-primary/90 text-white glow-primary" : "bg-secondary hover:bg-white/10"}`}
                  >
                    {checkoutKey === t.lookup_key ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Redirecting…</> : t.cta}
                  </Button>
                ) : (
                  <Link to="/login" className="mt-8">
                    <Button className="w-full rounded-sm bg-secondary hover:bg-white/10">{t.cta}</Button>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD MAGNET */}
      <section id="resource" className="relative border-t border-white/10 bg-background overflow-hidden">
        <div className="ambient-spot bottom-0 right-0 w-[400px] h-[400px] opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Eyebrow color="text-[hsl(var(--chart-2))]">Free Resource</Eyebrow>
            <h2 className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl mt-5">The 21 CFR Part 11 Readiness Checklist</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              A practical self-assessment covering electronic records, audit trails, e-signatures, access control and validation — the exact controls inspectors look for. Free PDF.
            </p>
            <ul className="mt-6 space-y-3">
              {["ALCOA+ electronic records", "Tamper-evident audit trails", "Electronic signatures & access control", "GAMP 5 validation & continuity"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-[hsl(var(--chart-2))] shrink-0" strokeWidth={2.5} /> {t}
                </li>
              ))}
            </ul>
          </div>
          <form onSubmit={downloadChecklist} data-testid="checklist-form" className="glass rounded-sm p-8">
            <p className="font-mono text-[11px] tracking-widest text-muted-foreground mb-4">GET THE CHECKLIST</p>
            <Input data-testid="checklist-email" required type="email" placeholder="Work email" className="rounded-sm bg-white/5 border-white/10 focus:border-primary mb-4 h-11" value={ce} onChange={(e) => setCe(e.target.value)} />
            <Button data-testid="checklist-submit" disabled={dl} className="w-full rounded-sm bg-[hsl(var(--chart-2))] hover:bg-[hsl(var(--chart-2))]/90 text-black h-11 font-medium glow-green">
              {dl ? "Preparing…" : "Download the Checklist"}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-3">No spam — we'll only follow up if you ask us to.</p>
            <Link to="/resources" data-testid="all-resources-link" className="inline-flex items-center gap-1 text-[hsl(var(--chart-2))] text-xs mt-4 hover:underline">
              See all free resources — ALCOA+ template & RPO/RTO calculator <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
            </Link>
          </form>
        </div>
      </section>

      {/* GET STARTED */}
      <section id="contact" className="relative border-t border-white/10 overflow-hidden">
        <div className="ambient-spot top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 py-28 flex flex-col items-start gap-8">
          <Eyebrow>Get started</Eyebrow>
          <h2 className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl max-w-2xl">Start protecting your data in minutes.</h2>
          <p className="text-muted-foreground leading-relaxed max-w-xl">
            No sales call, no cloud account to set up, no credit card to look around. Create an account and try the full platform for free — upgrade only when you're ready.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/login">
              <Button data-testid="get-started-btn" size="lg" className="rounded-sm bg-primary hover:bg-primary/90 text-white h-12 px-8 glow-primary hover:glow-primary-strong transition-shadow">
                Start free <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
            <a href={MAILTO} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
              <Mail className="h-4 w-4" strokeWidth={1.5} /> Questions? {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>

      <footer className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 bg-primary flex items-center justify-center glow-primary"><ShieldCheck className="h-3.5 w-3.5 text-white" strokeWidth={1.5} /></div>
            <span className="font-heading font-extrabold tracking-tighter">VAULTVEC</span>
          </div>
          <p className="font-mono text-xs text-muted-foreground">© 2026 Vaultvec Systems · Cyber protection for life sciences</p>
        </div>
      </footer>
    </div>
  );
}
